import { yupResolver } from '@hookform/resolvers/yup'
import { isArray } from 'lodash'
import React, {
  ChangeEvent,
  FunctionComponent,
  useEffect,
  useState
} from 'react'
import { Col, Row } from 'react-grid-system'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { array, object, string } from 'yup'

import {
  StyledLoadingContainer,
  StyledLoadingSpinner,
  StyledFormClinPageContentFrame,
  StyledHeaderClinPageContentFrame,
  StyledFixedWidthContainer,
  StyledContainer,
  StyledRow,
  StyledColumn,
  StyledClinButton,
  StyledInviteHeaderContainer,
  StyledInviteHeaderTitle,
  StyledInviteHeaderNavigation,
  StyledClinButtonMobile,
  StyledInviteAnnounce,
  StyledMessage,
  StyledDesktopOnly,
  StyledClinGroup,
  StyledMobileColumn
} from './InviteNewUser.styles'
import { ClinTheme } from '../../../ClinTheme'
import { ClinButton } from '../../../components/ClinButton'
import { ClinCheckbox } from '../../../components/ClinCheckbox'
import { ClinPageContentFrame } from '../../../components/ClinPageContentFrame'
import { ClinSelect, ClinSelectVariant } from '../../../components/ClinSelect'
import { ClinSpacer } from '../../../components/ClinSpacer'
import { ClinSpinner } from '../../../components/ClinSpinner'
import { StyledSpinnerContainer } from '../../../components/ClinSpinner/ClinSpinner.styles'
import { ClinText } from '../../../components/ClinText'
import { TypographyVariant } from '../../../components/ClinText/ClinText.styles'
import { ClinTextInput } from '../../../components/ClinTextInput'
import {
  UserRole,
  ContactTitles,
  IJobType,
  JobTypes,
  PhoneContactResponseType,
  phoneTypesRecord,
  userTitleRecord,
  countryCodes
} from '../../../constants'
import useEmailValidation from '../../../hooks/useCheckEmailError/useCheckEmailError'
import {
  ContactPhoneDto,
  CreateContactRequestDto,
  SpecialismDto
} from '../../../types/swaggerTypes'
import {
  getInputPlaceholder,
  PlaceholderType
} from '../../../utils/Forms/getInputPlaceholder'
import {
  getInputValidation,
  ValidationType
} from '../../../utils/Forms/getInputValidation'
import { requiredMinStringLength } from '../../../utils/Forms/requiredMinStringLength'
import { ClinIcon } from '../../../components/ClinIcon'
import { ClinIconPathName } from '../../../components/ClinIcon/ClinIcon.paths'

interface IInviteNewUserProps {
  /** Whether we are loading or not */
  isLoading: boolean
  /** An array of specialism values */
  specialisms: SpecialismDto[] | undefined
  /** The possible role types that can be assigned */
  roleTypes: UserRole[]
  /** Whether the submission API request is in progress */
  isSubmitting?: boolean
  /** Account number */
  instituteId?: number
  /** Form submission handler */
  handleFormSubmission: (data: CreateContactRequestDto) => void
  /** Cancel form and return to previous page */
  handleCancel: () => void
  /** Navigate to the previous page */
  handleGoBack: () => void
  /** An event for when a request is made to change the applications current institute */
  handleRequestInstituteChange: () => void
}

const defaultState: CreateContactRequestDto = {
  firstName: '',
  lastName: '',
  title: '',
  emailAddress: '',
  preferredName: '',
  licenseNumber: '',
  specialism: '',
  classification: '',
  phones: [
    {
      phoneId: '',
      phoneType: phoneTypesRecord['GEN'].type,
      countryCode: '',
      areaCode: '',
      phoneNumber: ''
    }
  ],
  roles: [
    {
      roleId: '',
      roleName: ''
    }
  ]
}

export const InviteNewUser: FunctionComponent<IInviteNewUserProps> = ({
  isLoading,
  specialisms,
  roleTypes,
  isSubmitting,
  instituteId,
  handleFormSubmission,
  handleCancel,
  handleGoBack,
  handleRequestInstituteChange
}) => {
  const { t } = useTranslation()

  const requiredValidationText = getInputValidation(
    ValidationType.RequiredField
  )

  const InviteNewUserSchema = object().shape({
    firstName: requiredMinStringLength(100),
    lastName: requiredMinStringLength(100),
    title: string(),
    emailAddress: string().required(requiredValidationText),
    preferredName: string().test(
      'len',
      getInputValidation(ValidationType.MaxiumumCharacters, '240'),
      (val) => {
        return val === '' || (val && val.length < 240) ? true : false
      }
    ),
    licenseNumber: string().when('classification', {
      is: (classification: string) =>
        classification.toLowerCase() === 'pharmacist' ||
        classification.toLowerCase() === 'physician',
      then: requiredMinStringLength(50)
    }),
    specialism: requiredMinStringLength(240),
    classification: string().test(
      'len',
      getInputValidation(ValidationType.MaxiumumCharacters, '240'),
      (val) => {
        return val === '' || (val && val.length < 240) ? true : false
      }
    ),
    phones: array()
      .compact()
      .of(
        object().shape(
          {
            phoneId: string(),
            phoneType: string(),
            requestType: string(),
            areaCode: string().when(['phoneNumber'], {
              is: (phoneNumber: any) => phoneNumber,
              then: requiredMinStringLength(20)
            }),
            phoneNumber: string().when(['areaCode'], {
              is: (areaCode: any) => areaCode,
              then: requiredMinStringLength(20)
            })
          },
          [['areaCode', 'phoneNumber']]
        )
      ),
    roles: array().of(
      object().shape({
        roleName: string()
      })
    )
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    control
  } = useForm<CreateContactRequestDto>({
    defaultValues: defaultState,
    resolver: yupResolver(InviteNewUserSchema)
  })
  const [isAdmin, setIsAdmin] = useState<boolean>(false)
  const { emailErrorMessage, checkEmail } = useEmailValidation()

  useEffect(() => {
    register('roles.0.roleName')
    setValue(
      'roles.0.roleName',
      roleTypes && isAdmin ? roleTypes[1].toString() : roleTypes[0].toString()
    )
  }, [isAdmin, register, roleTypes, setValue])

  const contactTitlesOptions = Object.values(ContactTitles)
  const watchClassification = watch('classification')
  const showLicenceNumber =
    watchClassification.toLowerCase() === 'pharmacist' ||
    watchClassification.toLowerCase() === 'physician'

  return (
    <ClinPageContentFrame isFullWidth={true}>
      {isLoading && (
        <Row justify="center">
          <Col width="auto">
            <StyledSpinnerContainer>
              <ClinSpinner size={ClinTheme.space[7]} />
            </StyledSpinnerContainer>
          </Col>
        </Row>
      )}
      {!isLoading && (
        <ClinPageContentFrame isFullWidth={true}>
          <StyledInviteHeaderContainer className="invite-user-header">
            <StyledHeaderClinPageContentFrame>
              <StyledInviteHeaderNavigation>
                <StyledClinButtonMobile
                  onClick={() => handleGoBack()}
                  variant="linkWithIcon"
                >
                  <ClinIcon
                    iconSize={ClinTheme.fontSizes[3]}
                    iconName={ClinIconPathName.ArrowLeft}
                  />
                </StyledClinButtonMobile>
                <StyledInviteHeaderTitle variant={TypographyVariant.H3}>
                  {t('invite_new_user:title')}
                </StyledInviteHeaderTitle>
              </StyledInviteHeaderNavigation>
              <ClinButton
                variant="linkWithIcon"
                onClick={() => handleRequestInstituteChange()}
              >
                <ClinIcon
                  style={{
                    margin: `${ClinTheme.space[1]} ${ClinTheme.space[1]} 0 0`
                  }}
                  iconSize={ClinTheme.fontSizes[4]}
                  iconName={ClinIconPathName.Switch}
                />
                <StyledDesktopOnly>
                  {t('invite_new_user:change_institute')}
                </StyledDesktopOnly>
              </ClinButton>
            </StyledHeaderClinPageContentFrame>
          </StyledInviteHeaderContainer>

          <StyledInviteAnnounce className="clin-announce">
            <ClinIcon
              iconSize={ClinTheme.fontSizes[6]}
              iconName={ClinIconPathName.Profile}
              iconFill={ClinTheme.colors.primaryLight}
            />
            <StyledMessage>
              <ClinText
                as="div"
                color={'inherit'}
                fontWeight={ClinTheme.fontWeights.medium}
                lineHeight={ClinTheme.lineHeights.heading[1]}
                fontSize={ClinTheme.fontSizes[0]}
              >
                {t('invite_new_user:account_number')} {instituteId}
              </ClinText>
              <ClinText
                as="div"
                color={'inherit'}
                fontWeight={ClinTheme.fontWeights.medium}
                lineHeight={ClinTheme.lineHeights.heading[0]}
                fontSize={ClinTheme.fontSizes[1]}
              >
                {t('invite_new_user:announce_description')}
              </ClinText>
            </StyledMessage>
          </StyledInviteAnnounce>

          <ClinSpacer height={ClinTheme.space[4]} />

          <form
            onSubmit={handleSubmit((values) => {
              const isEmailError = checkEmail(values.emailAddress)
              if (!isEmailError) {
                const updatedValues = values
                let filterPhoneResponses = null
                if (updatedValues.phones && updatedValues.phones.length > 0) {
                  filterPhoneResponses = updatedValues.phones.filter(
                    (item: ContactPhoneDto) => item.areaCode && item.phoneNumber
                  )
                  updatedValues.phones = filterPhoneResponses
                }
                handleFormSubmission(updatedValues)
              }
            })}
          >
            <StyledFormClinPageContentFrame>
              <StyledContainer>
                <StyledFixedWidthContainer width={736}>
                  <ClinText
                    fontWeight={ClinTheme.fontWeights.bold}
                    fontSize={ClinTheme.fontSizes[1]}
                    color={ClinTheme.colors.black}
                    lineHeight={ClinTheme.lineHeights.heading[0]}
                  >
                    {t('invite_new_user:account_details')}
                  </ClinText>

                  <ClinSpacer height={ClinTheme.space[3]} />

                  <StyledRow>
                    <StyledColumn width={360}>
                      <ClinTextInput
                        id="email-address"
                        {...register(`emailAddress`)}
                        label={t('invite_new_user:email_address_label')}
                        width="100%"
                        labelFontWeight={ClinTheme.fontWeights.normal}
                        hasError={!!emailErrorMessage}
                        prompt={emailErrorMessage}
                      />
                      <ClinSpacer height={ClinTheme.space[2]} />
                      <ClinText fontSize={ClinTheme.fontSizes[0]}>
                        {t('invite_new_user:email_address_info')}
                      </ClinText>
                    </StyledColumn>
                  </StyledRow>

                  <ClinSpacer height={ClinTheme.space[3]} />

                  <StyledRow>
                    <StyledColumn width={160}>
                      <Controller
                        name="title"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <ClinSelect
                            id="title"
                            label={t('invite_new_user:title_label')}
                            width="100%"
                            value={value}
                            variant={ClinSelectVariant.primaryV2}
                            onChange={(
                              changeValue: ChangeEvent<HTMLSelectElement>
                            ) => onChange(changeValue.currentTarget.value)}
                            hasError={!!(errors && errors.title)}
                            prompt={errors.title?.message}
                          >
                            <option value="" disabled={true}>
                              {getInputPlaceholder(PlaceholderType.SelectInput)}
                            </option>
                            {contactTitlesOptions.map(
                              (title: string, index) => {
                                return (
                                  <option
                                    key={`specialism${index}`}
                                    value={title}
                                  >
                                    {t(
                                      `${
                                        userTitleRecord[title as ContactTitles]
                                          .transKey
                                      }`
                                    )}
                                  </option>
                                )
                              }
                            )}
                          </ClinSelect>
                        )}
                      />
                    </StyledColumn>
                    <StyledColumn width={272}>
                      <ClinTextInput
                        id="first-name"
                        {...register(`firstName`)}
                        label={t('invite_new_user:first_name_label')}
                        width="100%"
                        maxLength={100}
                        labelFontWeight={ClinTheme.fontWeights.normal}
                        hasError={!!(errors && errors.firstName)}
                        prompt={errors.firstName?.message}
                      />
                    </StyledColumn>
                    <StyledColumn width={272}>
                      <ClinTextInput
                        id="last-name"
                        {...register(`lastName`)}
                        label={t('invite_new_user:last_name_label')}
                        width="100%"
                        maxLength={100}
                        labelFontWeight={ClinTheme.fontWeights.normal}
                        hasError={!!(errors && errors.lastName)}
                        prompt={errors.lastName?.message}
                      />
                    </StyledColumn>
                  </StyledRow>

                  <ClinSpacer height={ClinTheme.space[3]} />

                  <StyledRow>
                    {specialisms && (
                      <StyledColumn width={360}>
                        <Controller
                          name="specialism"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <ClinSelect
                              id="specialism"
                              label={t('invite_new_user:specialism_label')}
                              width="100%"
                              value={value}
                              variant={ClinSelectVariant.primaryV2}
                              onChange={(
                                changeValue: ChangeEvent<HTMLSelectElement>
                              ) => onChange(changeValue.currentTarget.value)}
                              hasError={!!(errors && errors.specialism)}
                              prompt={errors.specialism?.message}
                            >
                              <option value="" disabled={true}>
                                {getInputPlaceholder(
                                  PlaceholderType.SelectInput
                                )}
                              </option>
                              {specialisms
                                .sort((a, b) =>
                                  a.meaning.localeCompare(b.meaning)
                                )
                                .map((specialism: SpecialismDto, index) => {
                                  return (
                                    <option
                                      key={`specialism${index}`}
                                      value={specialism.code}
                                    >
                                      {specialism.meaning}
                                    </option>
                                  )
                                })}
                            </ClinSelect>
                          )}
                        />
                      </StyledColumn>
                    )}
                    <StyledColumn width={360}>
                      <Controller
                        name="classification"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <ClinSelect
                            id="classification"
                            label={t('invite_new_user:job_type_label')}
                            width="100%"
                            value={value}
                            variant={ClinSelectVariant.primaryV2}
                            onChange={(
                              changeValue: ChangeEvent<HTMLSelectElement>
                            ) => onChange(changeValue.currentTarget.value)}
                            hasError={!!(errors && errors.classification)}
                            prompt={errors.classification?.message}
                          >
                            <option value="" disabled={true}>
                              {getInputPlaceholder(PlaceholderType.SelectInput)}
                            </option>
                            {JobTypes.map((jobType: IJobType, index) => {
                              return (
                                <option
                                  key={`classification-${index}`}
                                  value={jobType.jobCode}
                                >
                                  {jobType.view}
                                </option>
                              )
                            })}
                          </ClinSelect>
                        )}
                      />
                    </StyledColumn>
                  </StyledRow>

                  <ClinSpacer height={ClinTheme.space[3]} />

                  {showLicenceNumber && (
                    <>
                      <StyledRow>
                        <StyledColumn>
                          <ClinTextInput
                            id="license-number"
                            {...register(`licenseNumber`)}
                            label={t('invite_new_user:licence_number')}
                            width="100%"
                            maxLength={50}
                            labelFontWeight={ClinTheme.fontWeights.normal}
                            hasError={!!(errors && errors.licenseNumber)}
                            prompt={errors.licenseNumber?.message}
                          />
                        </StyledColumn>
                      </StyledRow>

                      <ClinSpacer height={ClinTheme.space[3]} />
                    </>
                  )}

                  <StyledRow>
                    <StyledMobileColumn width={360}>
                      <StyledColumn width={172}>
                        <div style={{ display: 'none' }}>
                          <ClinTextInput
                            id="request-type"
                            {...register(`phones.0.requestType`)}
                            value={PhoneContactResponseType.CREATE}
                          />
                        </div>

                        <Controller
                          name="phones.0.countryCode"
                          control={control}
                          defaultValue={'44'}
                          render={({ field: { onChange, value } }) => {
                            !value &&
                              setValue('phones.0.countryCode', '44', {
                                shouldDirty: true
                              })
                            return (
                              <ClinSelect
                                id="country-code"
                                label={t('invite_new_user:country_code_label')}
                                width="100%"
                                defaultValue={value}
                                variant={ClinSelectVariant.primaryV2}
                                onChange={(
                                  changeValue: ChangeEvent<HTMLSelectElement>
                                ) => onChange(changeValue.currentTarget.value)}
                                hasError={
                                  Array.isArray(errors.phones) &&
                                  !!errors.phones[0]?.countryCode
                                }
                                prompt={
                                  isArray(errors.phones) &&
                                  errors.phones[0] !== undefined &&
                                  errors.phones[0].countryCode
                                    ? errors.phones[0].countryCode?.message
                                    : undefined
                                }
                              >
                                <option value="44">+44</option>
                                {countryCodes.map(
                                  (code: string, index: number) => {
                                    return (
                                      <option
                                        key={`code-${index}`}
                                        value={code}
                                      >
                                        {`+${code}`}
                                      </option>
                                    )
                                  }
                                )}
                              </ClinSelect>
                            )
                          }}
                        />
                      </StyledColumn>
                      <StyledColumn width={172}>
                        <ClinTextInput
                          id="area-code"
                          {...register(`phones.0.areaCode`)}
                          label={t('invite_new_user:area_code_label')}
                          width="100%"
                          type="tel"
                          maxLength={20}
                          labelFontWeight={ClinTheme.fontWeights.normal}
                          hasError={
                            Array.isArray(errors.phones) &&
                            !!errors.phones[0]?.areaCode
                          }
                          prompt={
                            isArray(errors.phones) &&
                            errors.phones[0] !== undefined &&
                            errors.phones[0].areaCode
                              ? errors.phones[0].areaCode?.message
                              : undefined
                          }
                        />
                      </StyledColumn>
                    </StyledMobileColumn>
                    <StyledColumn width={360}>
                      <ClinTextInput
                        id="phone-number"
                        {...register(`phones.0.phoneNumber`)}
                        label={t('invite_new_user:phone_number_label')}
                        width="100%"
                        type="tel"
                        maxLength={20}
                        labelFontWeight={ClinTheme.fontWeights.normal}
                        hasError={
                          Array.isArray(errors.phones) &&
                          !!errors.phones[0]?.phoneNumber
                        }
                        prompt={
                          isArray(errors.phones) &&
                          errors.phones[0] !== undefined &&
                          errors.phones[0].phoneNumber
                            ? errors.phones[0].phoneNumber?.message
                            : undefined
                        }
                      />
                    </StyledColumn>
                  </StyledRow>

                  <ClinSpacer height={ClinTheme.space[5]} />

                  <StyledRow>
                    <StyledColumn>
                      <ClinText
                        fontWeight={ClinTheme.fontWeights.bold}
                        fontSize={ClinTheme.fontSizes[1]}
                        color={ClinTheme.colors.black}
                        marginBottom={ClinTheme.space[3]}
                        lineHeight={ClinTheme.lineHeights.heading[0]}
                      >
                        {t('invite_new_user:permissions')}
                      </ClinText>
                      <ClinText
                        fontWeight={ClinTheme.fontWeights.bold}
                        fontSize={ClinTheme.fontSizes[1]}
                        color={ClinTheme.colors.black}
                        marginBottom={ClinTheme.space[3]}
                        lineHeight={ClinTheme.lineHeights.heading[3]}
                      >
                        {t('invite_new_user:is_this_an_administrator')}
                      </ClinText>

                      <ClinCheckbox
                        id="is-administrator"
                        label={t('invite_new_user:administrator_label')}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          setIsAdmin(event.currentTarget.checked)
                        }
                      />
                    </StyledColumn>
                  </StyledRow>

                  <ClinSpacer height={ClinTheme.space[5]} />

                  <StyledClinGroup>
                    <StyledLoadingContainer>
                      <StyledClinButton
                        type="submit"
                        variant="primary"
                        disabled={isSubmitting || !isValid}
                      >
                        {t('common:buttons.invite')}
                      </StyledClinButton>
                      {isSubmitting && (
                        <StyledLoadingSpinner>
                          <ClinSpinner />
                        </StyledLoadingSpinner>
                      )}
                    </StyledLoadingContainer>
                    <StyledClinButton onClick={() => handleCancel()}>
                      {t('common:buttons.cancel')}
                    </StyledClinButton>
                  </StyledClinGroup>
                </StyledFixedWidthContainer>
              </StyledContainer>
            </StyledFormClinPageContentFrame>
          </form>
        </ClinPageContentFrame>
      )}
    </ClinPageContentFrame>
  )
}
